import AppConfig from "@/config/app.config";

const SET_REFRESH_TOKEN_SECRET = "SET_REFRESH_TOKEN_SECRET";
const SET_REFRESH_TOKEN = "SET_REFRESH_TOKEN";
const SET_ACCESS_TOKEN = "SET_ACCESS_TOKEN";
const CLEAR_TOKENS = "CLEAR_TOKENS";
const TOKEN_CHECKED = "TOKEN_CHECKED";

const EXPIRATION_DELAY = 60 * 1000 * 5;
const MIN_INTERVAL = 60 * 1000 * 1;

const state = {
  accessToken: null,
  accessExpiresAt: null,
  refreshExpiresAt: null,
  refreshToken: null,
  isTokenChecked: false,
};

let timeout = null;

const mutations = {
  [SET_ACCESS_TOKEN](state, { accessToken, accessExpiresAt }) {
    state.accessToken = accessToken;
    state.accessExpiresAt = accessExpiresAt;
  },
  [SET_REFRESH_TOKEN](state, refreshExpiresAt) {
    state.refreshExpiresAt = refreshExpiresAt;
  },
  [SET_REFRESH_TOKEN_SECRET](state, refreshToken) {
    state.refreshToken = refreshToken;
  },
  [CLEAR_TOKENS](state) {
    state.accessToken = null;
    state.accessExpiresAt = null;
    state.refreshExpiresAt = null;
    state.refreshToken = null;
  },
  [TOKEN_CHECKED](state) {
    state.isTokenChecked = true;
  },
};

const actions = {
  refreshAccessToken({ commit, dispatch, getters, rootGetters }) {
    const platformId = rootGetters["platform/platformId"];

    const useBrowserAuth = AppConfig.authSessionMode === "browser";
    const refreshToken =
      getters.refreshToken ||
      (useBrowserAuth ? localStorage.getItem("refreshToken") : null);

    const body =
      useBrowserAuth && refreshToken && platformId
        ? { refreshToken, platformId }
        : {};

    return this.$http.patch("/users/sessions", body).then((res) => {
      const {
        accessToken,
        accessExpiresAt,
        refreshExpiresAt,
        refreshToken: newRefreshToken,
      } = res.data.token;

      localStorage.setItem("accessExpiresAt", accessExpiresAt);
      localStorage.setItem("refreshExpiresAt", refreshExpiresAt);

      if (useBrowserAuth) {
        localStorage.setItem("accessToken", accessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }
      }

      commit(SET_ACCESS_TOKEN, { accessToken, accessExpiresAt });
      commit(SET_REFRESH_TOKEN, refreshExpiresAt);

      if (newRefreshToken) {
        commit(SET_REFRESH_TOKEN_SECRET, newRefreshToken);
      }

      commit(TOKEN_CHECKED);
      return dispatch("user/handleSessionUpdate", res.data, { root: true });
    });
  },

  startRefreshCountdown({ dispatch, getters }) {
    if (!getters.accessExpiresAt) return;

    const expiration = new Date(getters.accessExpiresAt).getTime();
    const now = new Date().getTime();
    const interval = expiration - now - EXPIRATION_DELAY;

    if (interval > MIN_INTERVAL) {
      timeout = setTimeout(() => {
        dispatch("refreshAccessToken").catch((err) => {
          dispatch("user/handleRefreshFail", err, { root: true });
        });
      }, interval);
    }
  },

  setTokensData({ commit, dispatch }, payload) {
    const { accessToken, accessExpiresAt, refreshExpiresAt, refreshToken } =
      payload;

    localStorage.setItem("accessExpiresAt", accessExpiresAt);
    localStorage.setItem("refreshExpiresAt", refreshExpiresAt);

    const useBrowserAuth = AppConfig.authSessionMode === "browser";
    if (useBrowserAuth) {
      localStorage.setItem("accessToken", accessToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
    }

    commit(SET_ACCESS_TOKEN, { accessToken, accessExpiresAt });
    commit(SET_REFRESH_TOKEN, refreshExpiresAt);
    if (refreshToken) {
      commit(SET_REFRESH_TOKEN_SECRET, refreshToken);
    }

    commit(TOKEN_CHECKED);
    dispatch("startRefreshCountdown");
  },

  clearTokens({ commit }) {
    localStorage.removeItem("accessExpiresAt");
    localStorage.removeItem("refreshExpiresAt");
    localStorage.removeItem("refreshToken");
    clearTimeout(timeout);
    commit(CLEAR_TOKENS);
  },

  setTokenChecked({ commit }) {
    commit(TOKEN_CHECKED);
  },
};

const getters = {
  accessToken: (state) => state.accessToken,
  accessExpiresAt: (state) => state.accessExpiresAt,
  refreshExpiresAt: (state) => state.refreshExpiresAt,
  refreshToken: (state) => state.refreshToken,
  isTokenChecked: (state) => state.isTokenChecked,
  isAccessToken: (state) => state.isTokenChecked && state.accessToken,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
